import {
  HIGH_SCORE_LIMIT,
  createEmptyHighScoreDocument,
  mergeRaceRuns,
  parseHighScores,
  rankScores,
  serializeHighScores,
  type HighScore,
  type HighScoreDocument,
} from '../../sim/highScores/index.ts';
import { fetchRaceRuns, upsertRaceRuns } from './raceRunsRemote.ts';

/** Local cache of the best runs per map. Helmway `race_runs` is the source of truth. */
const STORAGE_KEY = 'battleboats:high-scores';
/** Set once this browser's pre-Helmway cache has been uploaded. */
const UPLOADED_KEY = 'battleboats:high-scores:uploaded';

function readDocument(): HighScoreDocument {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return createEmptyHighScoreDocument();
    }

    return parseHighScores(raw) ?? createEmptyHighScoreDocument();
  } catch {
    return createEmptyHighScoreDocument();
  }
}

function writeDocument(document: HighScoreDocument): void {
  try {
    localStorage.setItem(STORAGE_KEY, serializeHighScores(document));
  } catch {
    // Ignore storage failures; scores are best-effort.
  }
}

function localCacheUploaded(): boolean {
  try {
    return localStorage.getItem(UPLOADED_KEY) !== null;
  } catch {
    return false;
  }
}

function markLocalCacheUploaded(): void {
  try {
    localStorage.setItem(UPLOADED_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures; the next hydrate uploads again and duplicates are skipped.
  }
}

async function uploadLocalCache(document: HighScoreDocument): Promise<void> {
  const results = await Promise.all(
    Object.entries(document.scores).map(([mapId, scores]) => upsertRaceRuns(mapId, scores)),
  );

  if (results.every(Boolean)) {
    markLocalCacheUploaded();
  }
}

export function loadHighScores(mapId: string): HighScore[] {
  return readDocument().scores[mapId] ?? [];
}

/** Best run per catalog map, or an empty object when nothing is stored. */
export function loadBestScores(): Record<string, HighScore> {
  const best: Record<string, HighScore> = {};

  for (const [mapId, scores] of Object.entries(readDocument().scores)) {
    const top = scores[0];

    if (top) {
      best[mapId] = top;
    }
  }

  return best;
}

export function recordHighScore(mapId: string, incoming: HighScore): HighScore[] {
  return recordHighScores(mapId, [incoming]);
}

/**
 * Inserts each run, then keeps the best `HIGH_SCORE_LIMIT` for that map in the local cache.
 * Every run is also sent to Helmway in the background, including one that misses the top ten.
 */
export function recordHighScores(
  mapId: string,
  incoming: readonly HighScore[],
): HighScore[] {
  const document = readDocument();
  let ranked = document.scores[mapId] ?? [];

  for (const score of incoming) {
    ranked = rankScores(ranked, score, HIGH_SCORE_LIMIT);
  }

  document.scores[mapId] = ranked;
  writeDocument(document);
  void upsertRaceRuns(mapId, incoming);
  return ranked;
}

/**
 * Merges Helmway's best runs into the local cache so the synchronous loaders see them. Never
 * rejects: without a client, or when the fetch fails, the cache is left as it was.
 */
export async function hydrateRaceRuns(): Promise<void> {
  const remote = await fetchRaceRuns();

  if (remote === null) {
    return;
  }

  const local = readDocument();

  if (!localCacheUploaded()) {
    void uploadLocalCache(local);
  }

  writeDocument(mergeRaceRuns(remote, local));
}
